import { Package, Tag, Clock, RefreshCw, ArrowRight, ArrowDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { TaskConfig, CapabilityMetadata } from './types';

interface TaskConfigSummaryHeaderProps {
    config: TaskConfig;
    capability: CapabilityMetadata | null;
}

export function TaskConfigSummaryHeader({ config, capability }: TaskConfigSummaryHeaderProps) {
    const hasCustomInput = config.inputPath !== '$';
    const hasCustomOutput = config.resultPath !== '$.result' || config.resultPlacement !== 'merge';
    const retryCount = config.retry.reduce((sum, r) => sum + r.maxAttempts, 0);

    return (
        <div className="px-6 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50/30">
            <div className="flex items-center gap-6 flex-wrap">
                {/* Capability Key */}
                <div className="flex items-center gap-2">
                    <Package size={14} className="text-slate-400" />
                    <code className="text-sm font-mono font-medium text-slate-900">
                        {config.capabilityKey || 'Not selected'}
                        {capability?.version && (
                            <span className="text-slate-400">@{capability.version}</span>
                        )}
                    </code>
                </div>

                {/* Tags */}
                {capability?.tags && capability.tags.length > 0 && (
                    <div className="flex items-center gap-1.5">
                        <Tag size={12} className="text-slate-400" />
                        <div className="flex gap-1">
                            {capability.tags.slice(0, 3).map((tag) => (
                                <Badge
                                    key={tag}
                                    variant="secondary"
                                    className="text-[10px] px-1.5 py-0"
                                >
                                    {tag}
                                </Badge>
                            ))}
                            {capability.tags.length > 3 && (
                                <span className="text-[10px] text-slate-400">
                                    +{capability.tags.length - 3}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Divider */}
                <div className="h-4 w-px bg-slate-200" />

                {/* Input indicator */}
                {hasCustomInput && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                        <ArrowRight size={12} className="text-green-500" />
                        <span className="font-mono text-xs">{config.inputPath}</span>
                    </div>
                )}

                {/* Output indicator */}
                {hasCustomOutput && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                        <ArrowDown size={12} className="text-blue-500" />
                        <span className="font-mono text-xs">
                            {config.resultPlacement === 'discard'
                                ? 'discard'
                                : config.resultPlacement === 'replace'
                                    ? 'replace'
                                    : config.resultPath}
                        </span>
                    </div>
                )}

                {/* Timeout */}
                {config.timeoutSeconds && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Clock size={12} className="text-amber-500" />
                        <span>{config.timeoutSeconds}s</span>
                    </div>
                )}

                {/* Retry count */}
                {retryCount > 0 && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                        <RefreshCw size={12} className="text-purple-500" />
                        <span>{retryCount}x</span>
                    </div>
                )}

                {/* Execution mode badge */}
                {capability?.executionMode && capability.executionMode !== 'sync' && (
                    <Badge
                        variant="outline"
                        className={cn(
                            "text-[10px]",
                            capability.executionMode === 'job' && "border-orange-300 text-orange-600",
                            capability.executionMode === 'callback' && "border-purple-300 text-purple-600"
                        )}
                    >
                        {capability.executionMode}
                    </Badge>
                )}
            </div>

            {/* Description */}
            {capability?.description && (
                <p className="mt-2 text-sm text-slate-500 line-clamp-1">
                    {capability.description}
                </p>
            )}
        </div>
    );
}

